import { useState } from "react"
import { getUserToken } from "@/utils/userAuthenticate"; 
import instance from "@/axios";
import { useTranslation } from "react-i18next";
import "@/localisation/index"
import { BackButtonIcon, SaveButtonIcon, ExportIcon } from "@/components/Icons" ;
import SendTestEmail from "@/components/EmailTemplates/SendTestEmail"
import {sanitizeInput} from "@/hooks/useInputSanitization";
import { FaInfoCircle } from "react-icons/fa";
import MarkdownEditor from "@/components/EmailTemplates/RichTextEditor"

export default function CustomiseSelfBuyTemplate({setViewType, template}){
    const {t} = useTranslation()
    const [showTooltip, setShowTooltip] = useState(false);
    const [emailConfig, setEmailConfig] = useState({
        subject: template.emailConfig?.emailSubject || "",
        senderName: template.emailConfig?.senderName || "",
        senderEmail: template.emailConfig?.senderEmail || "",
    })
    const [customisationFields, setCustomisationFields] = useState({
        merchantInfo: template.updatedValues?.info || template.defaultValues.info || '',
        thankyouNote: template.updatedValues?.thankyouNote || template.defaultValues.thankyouNote || '',
    })
    const [bodyData, setBodyData] = useState({
        bodyMessage: "",
        errorMsg: "",
        emailError: "",
        isEmailSending: false,
        isSavingTemplate: false,
        templateBannerImage: template.updatedValues?.template_image || template.defaultValues.template_image
    })
    const [termsAndConditions, setTermsAndConditions] = useState(template.updatedValues?.terms || template.defaultValues?.terms || '')

    // Remove <ul> and </ul> from the stored content
    const cleanTerms = termsAndConditions.replace(/<\/?ul>/g, "");

    const saveTemplateChanges=async()=>{
        try {
            setBodyData((prev) => ({ ...prev, errorMsg: "", isSavingTemplate: true }))
            const url = "/mail-template/update";
            const headers = { Authorization: getUserToken() };
            const body = {
                templateId: template.id,
                updateObj: {
                    template_image: bodyData.templateBannerImage,
                    terms: cleanTerms,
                    info: customisationFields.merchantInfo,
                    thankyouNote: customisationFields.thankyouNote
                },
                emailConfig: {
                    emailSubject: emailConfig.subject,
                    senderName: emailConfig.senderName,
                    senderEmail: emailConfig.senderEmail
                }
            }
            const res = await instance.put(url, body, { headers });
            const {success, message} = res.data
            if(success){
                alert(t("emailTemplates.templateUpdated"))
                setViewType("TEMPLATE_LIST")
            }else {
                alert(message)
            }
        } catch (error) {
            console.log('error', error.message)
            alert(error.message)
        } finally {
            setBodyData((prev) => ({ ...prev, isSavingTemplate: false }))
        }
    }
    const BackButton=()=>(
        <div className="back-button" onClick={() => setViewType("TEMPLATE_LIST")}>
            <div className="icon-container">
                <BackButtonIcon />
            </div>
            <span className="button-text">{t("emailTemplates.back")}</span>
        </div>
    )
    const SaveButton=()=>(
        <div className="back-button" onClick={saveTemplateChanges} disabled={bodyData.isSavingTemplate}>
             <div className="icon-container">
                <SaveButtonIcon />
            </div>
            <span className="button-text">{t("emailTemplates.save")}</span>
        </div>
    )

    const handleAddImage =() => {
        const fileInput = document.getElementById("banner-file-picker");
        const file = fileInput.files[0];
        
        if (!file) {
            alert(t("createGiftcard.selectImage"));
            return;
        }
        const reader = new FileReader();

        reader.onloadend =() => {
            const img = new Image();
            img.onload =async () => {
                if (img.width === 600 && img.height === 250) {
                    const uploadedImageUrl = reader.result;
                    const brandImageEle = document.getElementById("template-banner-image");
                    brandImageEle && (brandImageEle.src = uploadedImageUrl);
                    setBodyData((prev) => ({ ...prev, templateBannerImage: uploadedImageUrl.split(",")[1] }));
                } else {
                    alert(t("createGiftcard.modelImageInvalid") + `${img.width}x${img.height}`);
                }
            };
    
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className="template-container">
            <div className="template-container__header">
                <BackButton />
                <div className="template-container__header__right">
                    <SendTestEmail templateId={template.id} type="buy-for-self" />
                    <SaveButton />
                </div>
            </div>
            <div className="template-container__body">
                <div className="product">
                    <div className="product__outline">
                        <div className="product__header">{t("emailTemplates.emailPreview")}</div>
                        {
                            bodyData.templateBannerImage && (
                                <div className="product__body">
                                    <div class="banner-image-container">
                                        <label for="banner-file-picker" class="image-picker-label">
                                            <ExportIcon />
                                            <span>{t("emailTemplates.editBannerImage")}</span>
                                            <input 
                                                type="file" 
                                                id="banner-file-picker" 
                                                onChange={handleAddImage} 
                                                accept="image/*" 
                                                hidden 
                                            />
                                        </label>
                                    </div>
                                </div>
                            )
                        }
                        <div dangerouslySetInnerHTML={{__html: template.html_content}} />
                    </div>
                </div>
                <div>
                    <div className="email-configuration">
                        <h2 className="email-configuration__heading">{t("emailTemplates.emailConfiguration")}</h2>
                        <div className="email-configuration__field">
                            <label htmlFor="email-subject" className="email-configuration__label">
                                {t("emailTemplates.emailSubject")}
                            </label>
                            <textarea 
                                rows={4}
                                id="email-subject"
                                placeholder={t("emailTemplates.enterEmailSubject")}
                                onInput={(e)=>setEmailConfig((prev)=>({...prev, subject: sanitizeInput(e.target.value, "textarea")}) )}
                                value={emailConfig.subject}
                            >
                                {emailConfig.subject}
                            </textarea>
                        </div>

                        <div className="email-configuration__field" id="sender-email">
                            <div>
                                <div className="tooltip-icon-container">
                                    <label htmlFor="sender-email" className="email-configuration__label">
                                        {t("emailTemplates.senderName")}
                                    </label>
                                    <div
                                        className="tooltip-icon"
                                        onMouseEnter={() => setShowTooltip(true)}
                                        onMouseLeave={() => setShowTooltip(false)}
                                    >
                                        <FaInfoCircle size={18} color="#888" />
                                        {showTooltip && (
                                        <span className="tooltip-text">
                                            {t("emailTemplates.tooltipInfo")}
                                        </span>
                                        )}
                                    </div>
                                </div>
                                <input
                                    type="email"
                                    id="sender-email"
                                    className="email-configuration__input"
                                    placeholder={t("emailTemplates.enterSenderName")}
                                    value={emailConfig.senderName}
                                    onChange={(e) =>
                                        setEmailConfig((prev) => ({ ...prev, senderName: sanitizeInput(e.target.value, 'email') }))
                                    }
                                />
                            </div>
                            <p className="error-msg">{bodyData.emailError}</p>
                        </div>
                        <div className="email-configuration__field" id="sender-email">
                            <div>
                                <label htmlFor="sender-email" className="email-configuration__label">
                                    {t("emailTemplates.senderEmail")}
                                </label>
                                <input
                                    type="email"
                                    id="sender-email"
                                    className="email-configuration__input"
                                    placeholder={t("emailTemplates.enterSenderEmail")}
                                    value={emailConfig.senderEmail}
                                    onChange={(e) =>
                                        setEmailConfig((prev) => ({ ...prev, senderEmail: sanitizeInput(e.target.value, 'email') }))
                                    }
                                />
                            </div>
                            <p className="error-msg">{bodyData.emailError}</p>
                        </div>
                    </div>
                    <div className="template-customisations">
                        <h4>{t("emailTemplates.templateCustomisations")}</h4>
                        <div>
                            <div className="template-customisations__field">
                                <label>{t("emailTemplates.merchantMessage")}</label>
                                <textarea value={customisationFields?.merchantInfo} onChange={(e)=>{setCustomisationFields((prev)=>({...prev, merchantInfo: sanitizeInput(e.target.value, 'textarea')}))}} rows={5}>
                                    {customisationFields?.merchantInfo}
                                </textarea>
                            </div>
                            <div className="template-customisations__field">
                                <label>{t("emailTemplates.thankyouMessage")}</label>
                                <textarea value={customisationFields?.thankyouNote} onChange={(e)=>{setCustomisationFields((prev)=>({...prev, thankyouNote: e.target.value}))}} rows={5}>
                                    {customisationFields?.thankyouNote}
                                </textarea>
                            </div>
                            <div className="template-customisations__field terms-and-conditions-editor">
                                <label>Add Terms and Conditions</label>
                                <MarkdownEditor termsAndConditions={termsAndConditions} setTermsAndConditions={setTermsAndConditions} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}